import { camelize } from 'casing'
import { KeywordItem } from '../../../modules/mpsSeo/reducer'

/** Toggle in dev to render the chart with API-shaped mock data for UI testing. */
export const USE_MOCK_GRAPH_DATA = false

const UPDATED_AT = '2026-08-03T00:00:50Z'
const MOCK_MONTH_COUNT = 6
const MOCK_POINTS_PER_MONTH = 4
const MOCK_POINTS_PER_COMPETITOR = MOCK_MONTH_COUNT * MOCK_POINTS_PER_MONTH

const COMPETITOR_IDS = {
  kion: 'e6359343-431b-411f-abfa-92ed6adf1936',
  ibm: 'e752e08e-a1d1-4939-9a04-0d68e0632b14',
  cloudZero: '1614cfa4-4cbb-48ff-bce1-931289fdd71b',
  vantage: 'aa18a6b7-e4f3-4aad-b5b8-824a98760f40',
  vmware: '4e3236e4-c287-4154-9c6c-d5a54be84536',
} as const

type SearchRankingApiRecord = {
  keyword_id: string
  keyword_name: string
  status: 'processed'
  updated_at: string
  self_rank_change: number
  self_latest_rank: number | null
  self_initial_rank: number
  competitors: Array<Record<string, unknown>>
}

const toApiTimestamp = (year: number, month: number, day: number, hour = 12) =>
  new Date(Date.UTC(year, month, day, hour, 0, 0))
    .toISOString()
    .replace('.000Z', 'Z')

/** 12 months × 4 snapshots per month = 48 points per competitor. */
const buildSeriesDates = ({
  startYear,
  startMonth,
  dayOffsets,
}: {
  startYear: number
  startMonth: number
  dayOffsets: readonly number[]
}) => {
  const dates: string[] = []

  for (let monthIndex = 0; monthIndex < MOCK_MONTH_COUNT; monthIndex += 1) {
    for (
      let pointIndex = 0;
      pointIndex < MOCK_POINTS_PER_MONTH;
      pointIndex += 1
    ) {
      dates.push(
        toApiTimestamp(
          startYear,
          startMonth + monthIndex,
          dayOffsets[pointIndex]
        )
      )
    }
  }

  return dates
}

const initialRank = (ranks: Array<number | null>) =>
  ranks.find(rank => rank !== null) ?? 0

const latestRank = (ranks: Array<number | null>) => {
  for (let index = ranks.length - 1; index >= 0; index -= 1) {
    if (ranks[index] !== null) {
      return ranks[index]
    }
  }

  return null
}

const rankChange = (initial: number, latest: number | null) => {
  if (!initial || latest === null) {
    return initial || 0
  }

  return initial - latest
}

const waveRanks = (
  count: number,
  baseRank: number,
  {
    trend = 0,
    offset = 0,
    nullEvery = 0,
  }: {
    trend?: number
    offset?: number
    nullEvery?: number
  } = {}
) =>
  Array.from({ length: count }, (_, index) => {
    if (nullEvery > 0 && index % nullEvery === 0) {
      return null
    }

    const wave = Math.sin((index + offset) / 2.4) * 2
    const rank = Math.round(baseRank + trend * index + wave + (index % 2))

    return Math.max(1, Math.min(40, rank))
  })

const buildCompetitor = ({
  _id,
  name,
  dates,
  ranks,
}: {
  _id: string
  name: string
  dates: string[]
  ranks: Array<number | null>
}) => {
  const initial = initialRank(ranks)
  const latest = latestRank(ranks)

  return {
    _id,
    name,
    rank: latest,
    rank_change: rankChange(initial, latest),
    initial_rank: initial,
    data: dates.map((created_at, index) => ({
      id: _id,
      name,
      rank: ranks[index] ?? null,
      created_at,
    })),
  }
}

const buildSelf = ({
  name,
  dates,
  ranks,
}: {
  name: string
  dates: string[]
  ranks: Array<number | null>
}) => {
  const initial = initialRank(ranks)
  const latest = latestRank(ranks)

  return {
    rank: latest,
    name,
    created_at: UPDATED_AT,
    rank_change: rankChange(initial, latest),
    initial_rank: initial,
    data: dates.map((created_at, index) => ({
      rank: ranks[index] ?? null,
      name,
      created_at,
    })),
  }
}

const buildKeywordRecord = ({
  keyword_id,
  keyword_name,
  competitors,
  self,
  selfFields = {},
}: {
  keyword_id: string
  keyword_name: string
  competitors: Array<Record<string, unknown>>
  self: ReturnType<typeof buildSelf>
  selfFields?: Partial<{
    self_rank_change: number
    self_latest_rank: number | null
    self_initial_rank: number
  }>
}) => ({
  keyword_id,
  keyword_name,
  status: 'processed' as const,
  updated_at: UPDATED_AT,
  self_rank_change:
    selfFields.self_rank_change ?? rankChange(self.initial_rank, self.rank),
  self_latest_rank: selfFields.self_latest_rank ?? self.rank,
  self_initial_rank: selfFields.self_initial_rank ?? self.initial_rank,
  competitors: [...competitors, self],
})

const FINOUT_SERIES_CONFIG = {
  startYear: 2026,
  startMonth: 1,
  dayOffsets: [3, 10, 18, 25] as const,
  baseRank: 8,
  trend: -0.15,
  nullEvery: 7,
}

const COMPETITOR_SERIES_CONFIG = [
  {
    _id: COMPETITOR_IDS.ibm,
    name: 'IBM Software',
    startYear: 2026,
    startMonth: 1,
    dayOffsets: [5, 12, 20, 27] as const,
    baseRank: 7,
    trend: 0.05,
    nullEvery: 0,
  },
  {
    _id: COMPETITOR_IDS.cloudZero,
    name: 'CloudZero',
    startYear: 2026,
    startMonth: 1,
    dayOffsets: [2, 11, 19, 26] as const,
    baseRank: 19,
    trend: -0.08,
    nullEvery: 8,
  },
  {
    _id: COMPETITOR_IDS.vantage,
    name: 'Vantage',
    startYear: 2026,
    startMonth: 1,
    dayOffsets: [4, 13, 21, 28] as const,
    baseRank: 12,
    trend: 0.1,
    nullEvery: 6,
  },
  {
    _id: COMPETITOR_IDS.vmware,
    name: 'VMware Tanzu CloudHealth',
    startYear: 2026,
    startMonth: 1,
    dayOffsets: [6, 14, 22, 29] as const,
    baseRank: 24,
    trend: 0.03,
    nullEvery: 9,
  },
  {
    _id: COMPETITOR_IDS.kion,
    name: 'Kion',
    startYear: 2026,
    startMonth: 1,
    dayOffsets: [1, 9, 17, 24] as const,
    baseRank: 15,
    trend: -0.04,
    nullEvery: 5,
  },
] as const

type SeriesSchedule = {
  startYear: number
  startMonth: number
  dayOffsets: readonly number[]
  baseRank: number
  trend: number
  nullEvery: number
}

const buildSeriesBundle = (config: SeriesSchedule, offset: number) => {
  const dates = buildSeriesDates({
    startYear: config.startYear,
    startMonth: config.startMonth,
    dayOffsets: config.dayOffsets,
  })

  const ranks = waveRanks(dates.length, config.baseRank, {
    trend: config.trend,
    offset,
    nullEvery: config.nullEvery,
  })

  return { dates, ranks }
}

const buildUnitEconomicsKeyword = (): SearchRankingApiRecord => {
  const finoutBundle = buildSeriesBundle(FINOUT_SERIES_CONFIG, 0)
  const finout = buildSelf({
    name: 'Finout',
    dates: finoutBundle.dates,
    ranks: finoutBundle.ranks,
  })

  return buildKeywordRecord({
    keyword_id: '82c1a565-9149-44f3-8b7b-b7d4de9ff186',
    keyword_name: 'unit economics',
    self: finout,
    selfFields: {
      self_latest_rank: latestRank(finoutBundle.ranks),
      self_initial_rank: initialRank(finoutBundle.ranks),
      self_rank_change: rankChange(
        initialRank(finoutBundle.ranks),
        latestRank(finoutBundle.ranks)
      ),
    },
    competitors: COMPETITOR_SERIES_CONFIG.map((config, index) => {
      const bundle = buildSeriesBundle(config, index + 1)

      return buildCompetitor({
        _id: config._id,
        name: config.name,
        dates: bundle.dates,
        ranks: bundle.ranks,
      })
    }),
  })
}

const buildCostAllocationKeyword = (): SearchRankingApiRecord => {
  const finoutDates = buildSeriesDates({
    startYear: 2026,
    startMonth: 0,
    dayOffsets: [4, 12, 20, 28],
  })
  const finoutRanks = waveRanks(finoutDates.length, 11, {
    trend: -0.1,
    nullEvery: 6,
  })
  const finout = buildSelf({
    name: 'Finout',
    dates: finoutDates,
    ranks: finoutRanks,
  })

  return buildKeywordRecord({
    keyword_id: '9e6a9c80-5cc1-41bd-a352-09d5ce8b780f',
    keyword_name: 'cost allocation',
    self: finout,
    competitors: [
      buildCompetitor({
        _id: COMPETITOR_IDS.ibm,
        name: 'IBM Software',
        dates: buildSeriesDates({
          startYear: 2026,
          startMonth: 0,
          dayOffsets: [2, 10, 18, 26],
        }),
        ranks: waveRanks(MOCK_POINTS_PER_COMPETITOR, 6, { offset: 1 }),
      }),
      buildCompetitor({
        _id: COMPETITOR_IDS.cloudZero,
        name: 'CloudZero',
        dates: buildSeriesDates({
          startYear: 2026,
          startMonth: 0,
          dayOffsets: [6, 14, 22, 30],
        }),
        ranks: waveRanks(MOCK_POINTS_PER_COMPETITOR, 3, { offset: 2 }),
      }),
    ],
  })
}

/** Raw `/seo_records` API payload (snake_case). */
export const MOCK_SEARCH_RANKING_API_RESPONSE = {
  records: [buildUnitEconomicsKeyword(), buildCostAllocationKeyword()],
}

/** Same transform as `searchRanking`: `camelize(response.data.records)`. */
export const MOCK_KEYWORD_RANKING_RECORDS = camelize(
  MOCK_SEARCH_RANKING_API_RESPONSE.records
) as KeywordItem[]

/** Primary chart mock — 12 months × 4 points/month per competitor (48 each). */
export const MOCK_KEYWORD_ITEM_FOR_CHART =
  MOCK_KEYWORD_RANKING_RECORDS.find(
    record => record.keywordName === 'unit economics'
  ) ?? MOCK_KEYWORD_RANKING_RECORDS[0]

export const MOCK_DATA_SHAPE = {
  monthCount: MOCK_MONTH_COUNT,
  pointsPerMonth: MOCK_POINTS_PER_MONTH,
  pointsPerCompetitor: MOCK_POINTS_PER_COMPETITOR,
}
